import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react'

const CartContext = createContext(null)

const STORAGE_KEY = 'sadeap_cart_v1'

const loadInitial = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'ADD': {
            const { product, quantity = 1 } = action
            const existing = state.find((item) => item.id === product.id)
            if (existing) {
                return state.map((item) =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                )
            }
            return [
                ...state,
                {
                    id: product.id,
                    name: product.name,
                    image: product.image,
                    categoryName: product.categoryName,
                    quantity,
                },
            ]
        }
        case 'REMOVE':
            return state.filter((item) => item.id !== action.id)
        case 'SET_QUANTITY': {
            const qty = Math.max(1, action.quantity)
            return state.map((item) =>
                item.id === action.id ? { ...item, quantity: qty } : item
            )
        }
        case 'INCREMENT':
            return state.map((item) =>
                item.id === action.id ? { ...item, quantity: item.quantity + 1 } : item
            )
        case 'DECREMENT':
            return state
                .map((item) =>
                    item.id === action.id ? { ...item, quantity: item.quantity - 1 } : item
                )
                .filter((item) => item.quantity > 0)
        case 'CLEAR':
            return []
        default:
            return state
    }
}

export const CartProvider = ({ children }) => {
    const [cart, dispatch] = useReducer(reducer, undefined, loadInitial)
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cart))
        } catch {
            /* ignore quota errors */
        }
    }, [cart])

    const value = useMemo(() => {
        const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0)
        return {
            cart,
            totalItems,
            isOpen,
            openCart: () => setIsOpen(true),
            closeCart: () => setIsOpen(false),
            toggleCart: () => setIsOpen((v) => !v),
            addToCart: (product, quantity = 1) =>
                dispatch({ type: 'ADD', product, quantity }),
            removeFromCart: (id) => dispatch({ type: 'REMOVE', id }),
            setQuantity: (id, quantity) =>
                dispatch({ type: 'SET_QUANTITY', id, quantity }),
            increment: (id) => dispatch({ type: 'INCREMENT', id }),
            decrement: (id) => dispatch({ type: 'DECREMENT', id }),
            clearCart: () => dispatch({ type: 'CLEAR' }),
        }
    }, [cart, isOpen])

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
    const ctx = useContext(CartContext)
    if (!ctx) throw new Error('useCart must be used within a CartProvider')
    return ctx
}
